import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useI18n } from '../i18n';
import { localizePasswordResetFailureMessage } from '../i18n/localizeServerMessage';
import { formatIsraeliPhoneInput, formatPhoneForDisplay } from '../utils/phoneE164';
import type { LoginOption } from './loginConstants';
import { isValidEmailAddress } from './emailOtpAuth';
import { isPhoneOtpEnabled, isValidOtpPhone } from './phoneOtpAuth';
import {
  completePasswordReset,
  normalizeOtpCode,
  sendPasswordResetEmailOtp,
  sendPasswordResetPhoneOtp,
  verifyPasswordResetEmailOtp,
  verifyPasswordResetPhoneOtp,
} from './passwordResetAuth';

export type PasswordResetChannel = 'email' | 'phone';
export type PasswordResetStep = 'identifier' | 'code' | 'password' | 'done';

export function usePasswordReset(loginType: LoginOption) {
  const { t } = useI18n();
  const phoneOtpEnabled = isPhoneOtpEnabled();
  const [channel, setChannel] = useState<PasswordResetChannel>(() =>
    isPhoneOtpEnabled() ? 'phone' : 'email',
  );
  const [step, setStep] = useState<PasswordResetStep>('identifier');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorFriendly, setErrorFriendly] = useState<string | null>(null);
  const [codeSent, setCodeSent] = useState(false);
  const resetTokenRef = useRef<string | null>(null);

  const clearErrors = useCallback(() => {
    setErrorFriendly(null);
  }, []);

  const resetTransientState = useCallback(() => {
    setStep('identifier');
    setCode('');
    setNewPassword('');
    setConfirmPassword('');
    setCodeSent(false);
    resetTokenRef.current = null;
    clearErrors();
  }, [clearErrors]);

  useEffect(() => {
    resetTransientState();
  }, [loginType, resetTransientState]);

  const onChangeChannel = useCallback(
    (next: PasswordResetChannel) => {
      if (next === channel) {
        return;
      }
      setChannel(next);
      resetTransientState();
    },
    [channel, resetTransientState],
  );

  const onChangeEmail = useCallback(
    (value: string) => {
      setEmail(value);
      clearErrors();
    },
    [clearErrors],
  );

  const onChangePhone = useCallback(
    (value: string) => {
      setPhone(formatIsraeliPhoneInput(value));
      clearErrors();
    },
    [clearErrors],
  );

  const onChangeCode = useCallback(
    (value: string) => {
      setCode(value);
      clearErrors();
    },
    [clearErrors],
  );

  const onChangeNewPassword = useCallback(
    (value: string) => {
      setNewPassword(value);
      clearErrors();
    },
    [clearErrors],
  );

  const onChangeConfirmPassword = useCallback(
    (value: string) => {
      setConfirmPassword(value);
      clearErrors();
    },
    [clearErrors],
  );

  const onBackToIdentifier = useCallback(() => {
    resetTransientState();
  }, [resetTransientState]);

  const localizeFailure = useCallback(
    (friendly: string, activeChannel: PasswordResetChannel = channel) =>
      localizePasswordResetFailureMessage(friendly, t, activeChannel),
    [channel, t],
  );

  const onSendCode = useCallback(async () => {
    if (submitting) {
      return;
    }
    if (channel === 'email') {
      const trimmedEmail = email.trim();
      if (!isValidEmailAddress(trimmedEmail)) {
        setErrorFriendly(t('auth.portal.invalidEmail'));
        return;
      }
      setSubmitting(true);
      clearErrors();
      try {
        const result = await sendPasswordResetEmailOtp(trimmedEmail, loginType);
        if (result.ok) {
          setEmail(trimmedEmail);
          setStep('code');
          setCodeSent(true);
          return;
        }
        setErrorFriendly(
          result.userNotFound
            ? t('auth.forgotPassword.userNotFound')
            : localizeFailure(result.friendly, 'email'),
        );
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (!isValidOtpPhone(phone)) {
      setErrorFriendly(t('auth.portal.invalidPhone'));
      return;
    }
    setSubmitting(true);
    clearErrors();
    try {
      const result = await sendPasswordResetPhoneOtp(phone, loginType);
      if (result.ok) {
        setStep('code');
        setCodeSent(true);
        return;
      }
      setErrorFriendly(
        result.userNotFound
          ? t('auth.forgotPassword.phoneUserNotFound')
          : localizeFailure(result.friendly, 'phone'),
      );
    } finally {
      setSubmitting(false);
    }
  }, [channel, clearErrors, email, localizeFailure, loginType, phone, submitting, t]);

  const onVerifyCode = useCallback(async () => {
    if (submitting) {
      return;
    }
    const trimmedCode = normalizeOtpCode(code);
    if (trimmedCode.length < 4) {
      setErrorFriendly(
        channel === 'email' ? t('auth.portal.invalidCode') : t('auth.portal.invalidSmsCode'),
      );
      return;
    }

    setSubmitting(true);
    clearErrors();
    try {
      const result =
        channel === 'email'
          ? await verifyPasswordResetEmailOtp(email.trim(), trimmedCode, loginType)
          : await verifyPasswordResetPhoneOtp(phone, trimmedCode, loginType);
      if (!result.ok) {
        setErrorFriendly(localizeFailure(result.friendly));
        return;
      }
      resetTokenRef.current = result.resetToken;
      setStep('password');
      setCode('');
    } finally {
      setSubmitting(false);
    }
  }, [channel, clearErrors, code, email, localizeFailure, loginType, phone, submitting, t]);

  const onSubmitNewPassword = useCallback(async () => {
    if (submitting) {
      return;
    }
    const password = newPassword.trim();
    const confirm = confirmPassword.trim();
    if (!password) {
      setErrorFriendly(t('auth.forgotPassword.passwordRequired'));
      return;
    }
    if (password !== confirm) {
      setErrorFriendly(t('auth.forgotPassword.mismatch'));
      return;
    }
    const resetToken = resetTokenRef.current;
    if (!resetToken) {
      setErrorFriendly(t('auth.forgotPassword.noResetToken'));
      setStep('identifier');
      return;
    }

    setSubmitting(true);
    clearErrors();
    try {
      const result = await completePasswordReset(resetToken, password);
      if (!result.ok) {
        setErrorFriendly(localizeFailure(result.friendly));
        return;
      }
      resetTokenRef.current = null;
      setNewPassword('');
      setConfirmPassword('');
      setStep('done');
    } finally {
      setSubmitting(false);
    }
  }, [clearErrors, confirmPassword, localizeFailure, newPassword, submitting, t]);

  const onSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (step === 'identifier') {
        void onSendCode();
        return;
      }
      if (step === 'code') {
        void onVerifyCode();
        return;
      }
      if (step === 'password') {
        void onSubmitNewPassword();
      }
    },
    [onSendCode, onSubmitNewPassword, onVerifyCode, step],
  );

  const displayPhone = formatPhoneForDisplay(phone || '');
  const passwordsMismatch =
    Boolean(newPassword.trim()) &&
    Boolean(confirmPassword.trim()) &&
    newPassword.trim() !== confirmPassword.trim();

  return {
    phoneOtpEnabled,
    channel,
    step,
    email,
    phone,
    displayPhone,
    code,
    newPassword,
    confirmPassword,
    passwordsMismatch,
    submitting,
    codeSent,
    errorFriendly,
    onChangeChannel,
    onChangeEmail,
    onChangePhone,
    onChangeCode,
    onChangeNewPassword,
    onChangeConfirmPassword,
    onBackToIdentifier,
    onSubmit,
  };
}
